import { Component, h } from 'preact'
import Panel from '../panel'
import ButtonControl from '../button-control'
import axios from 'axios'
import RadioInputGroup from '../ui-components/RadioInputGroup'
import InputGroup from '../ui-components/InputGroup'
import AlertComponent from '../ui-components/AlertComponent'
import ConfirmationModal from '../ui-components/ConfirmationModal'
import { isHtmlElement } from '../../libs/preact-entry'
import Header from '../header'
import Switcher from '../control/Switcher'
import SalesforceFederatedSearch from './SalesforceFederatedSearch'
import tracker from '../../components/tracker/publish'
import { common } from '../common'
import { InformationCallout } from '../ui-components/InformationCallout'

interface Props {
   csrfToken: string,
   isProduction: boolean
}

interface SalesforceData {
    type: string,
    username: string,
    instanceUrl: string,
    appConsumerKey: string
}

interface SyncAccountMappingData {
  fieldApiName: string,
  valueApiName: string
}

interface SyncAccountMappingDataError {
  showErrorFieldApiName: boolean,
  showErrorValueApiName: boolean
}

interface StoredSyncAccountDataSettings {
  syncAccountMappingData: SyncAccountMappingData,
  enableSyncAccountData: boolean
}

interface State extends SalesforceData {
    isLoading?: boolean,
    isInstalled: boolean,
    messageType?: string|null,
    message?: string,
    showConfirmationMessage: boolean,
    enableCRM: boolean,
    enableEscalation: boolean,
    enableFederatedSearch: boolean,
    federatedSearchLanguage: string,
    enableSyncAccountData: boolean,
    syncAccountMappingData: SyncAccountMappingData,
    storedSyncAccountDataSettings: StoredSyncAccountDataSettings,
    syncAccountMappingDataError: SyncAccountMappingDataError,
    showActionButtonsForSyncAccountDataSetting: boolean
}

class SalesforceIntegration extends Component<Props, State> {
  downloadLink: string
  headerDescription: string
  isSalesforceEnabled: boolean

  constructor (props: Props) {
    super(props)
    this.state = {
      isLoading: false,
      type: '',
      username: '',
      instanceUrl: '',
      appConsumerKey: '',
      isInstalled: false,
      messageType: null,
      message: '',
      showConfirmationMessage: false,
      enableCRM: false,
      enableEscalation: false,
      enableFederatedSearch: false,
      federatedSearchLanguage: '',
      enableSyncAccountData: false,
      syncAccountMappingData: this.createDefaultAccountMapping(),
      storedSyncAccountDataSettings: {
        syncAccountMappingData: this.createDefaultAccountMapping(),
        enableSyncAccountData: false
      },
      syncAccountMappingDataError: {
        showErrorFieldApiName: false,
        showErrorValueApiName: false
      },
      showActionButtonsForSyncAccountDataSetting: false
    }

    this.headerDescription = 'Push community member and activity data to Salesforce to gain insights via a 360 degree customer view.'
    this.downloadLink = '/integration/salesforce/download'
    this.isSalesforceEnabled = (common.isModuleActive('salesforceCrm') || common.isModuleActive('salesforceFederatedSearch') || common.isModuleActive('salesforceCaseEscalation') || common.isModuleActive('salesforceSyncAccountData'))
    this.onTypeChanged = this.onTypeChanged.bind(this)
    this.onUsernameChanged = this.onUsernameChanged.bind(this)
    this.onInstanceUrlChanged = this.onInstanceUrlChanged.bind(this)
    this.onAppConsumerKeyChanged = this.onAppConsumerKeyChanged.bind(this)
    this.onSave = this.onSave.bind(this)
    this.onShowConfirmationMessage = this.onShowConfirmationMessage.bind(this)
    this.onHideConfirmationMessage = this.onHideConfirmationMessage.bind(this)
    this.onDelete = this.onDelete.bind(this)
    this.onCRMToggle = this.onCRMToggle.bind(this)
    this.onAccountMappingFieldApiNameChanged = this.onAccountMappingFieldApiNameChanged.bind(this)
    this.onAccountMappingPicklistValueChanged = this.onAccountMappingPicklistValueChanged.bind(this)
    this.onSyncAccountDataToggle = this.onSyncAccountDataToggle.bind(this)
    this.saveAccountSyncDataSettings = this.saveAccountSyncDataSettings.bind(this)
    this.resetAccountSyncDataSettings = this.resetAccountSyncDataSettings.bind(this)
    this.onSyncAccountMappingInputClicked = this.onSyncAccountMappingInputClicked.bind(this)
  }

  async componentDidMount () {
    if (!this.isSalesforceEnabled) {
      return
    }
    this.setState({ isLoading: true, messageType: null })
    try {
      const res = await axios.get('/integration/salesforce/settings', {
        validateStatus: (state: number) => {
          return (state >= 200 && state < 300) || state === 403
        }
      })
      if (Object.keys(res.data).length !== 0) {
        const instanceUrl = res.data.instanceUrl.replace('.salesforce.com', '')
        this.setState({
          type: res.data.type,
          username: res.data.username,
          instanceUrl: instanceUrl,
          appConsumerKey: res.data.appConsumerKey,
          isInstalled: res.data.isInstalled,
          enableCRM: res.data.crmEnabled,
          enableEscalation: res.data.caseEscalationEnabled,
          enableFederatedSearch: res.data.federatedSearchEnabled,
          federatedSearchLanguage: res.data.federatedSearchLanguage || '',
          enableSyncAccountData: res.data.syncAccountDataEnabled,
          syncAccountMappingData: res.data.syncAccountDataMapping || this.createDefaultAccountMapping(),
          storedSyncAccountDataSettings: {
            syncAccountMappingData: res.data.syncAccountDataMapping,
            enableSyncAccountData: res.data.syncAccountDataEnabled
          }
        })
      }
      this.setState({ isLoading: false })
    } catch (e) {
      this.setState({ messageType: 'error', message: 'Unable to retrieve salesforce configuration.', isLoading: false })
    }
  }

  onTypeChanged (value: string): void {
    this.setState({ type: value })
  }

  onUsernameChanged (e: ChangeEvent) {
    this.setState({ username: e.target.value })
  }

  onInstanceUrlChanged (e: ChangeEvent) {
    const url = e.target.value
    const formattedUrl = url.replace(/^(?:https?:\/\/)/i, '').split('/')[0]
    this.setState({ instanceUrl: formattedUrl })
  }

  onAppConsumerKeyChanged (e: ChangeEvent) {
    this.setState({ appConsumerKey: e.target.value })
  }

  onShowConfirmationMessage () {
    this.setState({ showConfirmationMessage: true })
  }

  onHideConfirmationMessage () {
    this.setState({ showConfirmationMessage: false })
  }

  handleOpenEmbeddables () {
    const embeddablesButton = document.querySelector('.insided-conversational-trigger-button')

    if (isHtmlElement(embeddablesButton)) {
      embeddablesButton.click()
    }
  }

  async onDelete () {
    this.setState({ isLoading: true, messageType: null })
    const errorMessage = 'Unable to delete credentials for salesforce integration.'
    try {
      await axios.delete('/integration/salesforce/settings', { data: { csrfToken: this.props.csrfToken } })
      this.setState({
        type: '',
        username: '',
        instanceUrl: '',
        appConsumerKey: '',
        isInstalled: false,
        isLoading: false,
        enableCRM: false,
        enableEscalation: false,
        enableFederatedSearch: false,
        federatedSearchLanguage: '',
        syncAccountMappingData: this.createDefaultAccountMapping(),
        enableSyncAccountData: false
      })
    } catch (e) {
      this.setState({ messageType: 'error', message: errorMessage, isLoading: false })
    }
    this.onHideConfirmationMessage()
  }

  async onSave () {
    this.setState({ messageType: null, isLoading: true })
    const successMessage = 'Salesforce integration is installed successfully!'
    let errorMessage: string = 'Unable to save configuration for salesforce integration.'
    try {
      await axios.post('/integration/salesforce/settings', {
        type: this.state.type,
        username: this.state.username,
        instanceUrl: this.state.instanceUrl.concat('.salesforce.com'),
        appConsumerKey: this.state.appConsumerKey,
        csrfToken: this.props.csrfToken
      })
      this.setState({ isInstalled: true, messageType: 'success', message: successMessage, isLoading: false })
    } catch (e) {
      if (e.response.data) {
        errorMessage = e.response.data.error.reduce((accum: string, item: string) => {
          accum += '<br>' + item
          return accum
        })
      }

      this.setState({ messageType: 'error', message: errorMessage, isLoading: false })
    }
  }

  async onCRMToggle () {
    this.setState({ messageType: null })
    const successMessage = 'Changes are saved successfully'
    let errorMessage: string = 'Changes could not be saved'
    try {
      await axios.post('/integration/salesforce/toggleCRM', {
        isActive: !this.state.enableCRM
      })
      this.setState({ enableCRM: !this.state.enableCRM, message: successMessage, messageType: 'success' })
    } catch (e) {
      this.setState({ messageType: 'error', message: errorMessage })
    }
  }

  async onCaseEscalationToggle () {
    this.setState({ messageType: null })
    const successMessage = 'Changes are saved successfully'
    let errorMessage: string = 'Changes could not be saved'
    try {
      await axios.post('/integration/salesforce/toggleCaseEscalation', {
        caseEscalationEnabled: !this.state.enableEscalation
      })
      this.setState({ enableEscalation: !this.state.enableEscalation, message: successMessage, messageType: 'success' })
      tracker.trackEvent('userevent', {
        type: `Salesforce case integration ${this.state.enableEscalation ? 'enabled' : 'disabled'}`
      })
    } catch (e) {
      this.setState({ messageType: 'error', message: errorMessage })
    }
  }

  onAccountMappingFieldApiNameChanged (e: ChangeEvent) {
    const { valueApiName } = this.state.syncAccountMappingData
    const updatedFieldApiName = e.target.value.trim()
    this.setState({ syncAccountMappingData: { fieldApiName: updatedFieldApiName, valueApiName: valueApiName }, showActionButtonsForSyncAccountDataSetting: true })
  }

  onAccountMappingPicklistValueChanged (e: ChangeEvent) {
    const { fieldApiName } = this.state.syncAccountMappingData
    const updatedValueApiName = e.target.value.trim()
    this.setState({ syncAccountMappingData: { fieldApiName: fieldApiName, valueApiName: updatedValueApiName }, showActionButtonsForSyncAccountDataSetting: true })
  }

  onSyncAccountDataToggle () {
    const { enableSyncAccountData } = this.state
    this.setState({ enableSyncAccountData: !enableSyncAccountData, showActionButtonsForSyncAccountDataSetting: true })
  }

  async saveAccountSyncDataSettings () {
    this.setState({ messageType: null, isLoading: true })
    const successMessage = 'Changes are saved successfully'
    let errorMessage: string = 'Changes could not be saved'
    const { fieldApiName, valueApiName } = this.state.syncAccountMappingData
    const { enableSyncAccountData } = this.state
    const fieldApiNameHasError = !fieldApiName || fieldApiName.length === 0
    const valueApiNameHasError = !valueApiName || valueApiName.length === 0
    if (enableSyncAccountData && (fieldApiNameHasError || valueApiNameHasError)) {
      this.setState({
        syncAccountMappingDataError: {
          showErrorFieldApiName: fieldApiNameHasError,
          showErrorValueApiName: valueApiNameHasError
        },
        isLoading: false
      })
      return
    }

    try {
      await axios.post('/integration/salesforce/syncAccountDataSettings', {
        syncAccountData: enableSyncAccountData,
        syncAccountMappingData: this.state.syncAccountMappingData
      })
      const updatedSyncAccountMappingData = {
        fieldApiName: fieldApiName,
        valueApiName: valueApiName
      }
      this.setState({
        storedSyncAccountDataSettings: { syncAccountMappingData: updatedSyncAccountMappingData, enableSyncAccountData: enableSyncAccountData },
        showActionButtonsForSyncAccountDataSetting: false,
        messageType: 'success',
        message: successMessage,
        isLoading: false
      })

      tracker.trackEvent('userevent', {
        type: `Salesforce sync account data ${enableSyncAccountData ? 'enabled' : 'disabled'}`
      })
    } catch (e) {
      const { enableSyncAccountData, syncAccountMappingData } = this.state.storedSyncAccountDataSettings
      this.setState({
        messageType: 'error',
        message: errorMessage,
        isLoading: false,
        enableSyncAccountData: enableSyncAccountData,
        syncAccountMappingData: syncAccountMappingData
      })
    }
  }

  onSyncAccountMappingInputClicked () {
    const { showErrorFieldApiName, showErrorValueApiName } = this.state.syncAccountMappingDataError
    if (!showErrorFieldApiName && !showErrorValueApiName) {
      this.setState({
        showActionButtonsForSyncAccountDataSetting: true
      })
      return
    }

    this.setState({
      syncAccountMappingDataError: {
        showErrorFieldApiName: false,
        showErrorValueApiName: false
      },
      showActionButtonsForSyncAccountDataSetting: true
    })
  }

  resetAccountSyncDataSettings () {
    const { syncAccountMappingData, enableSyncAccountData } = this.state.storedSyncAccountDataSettings

    this.setState({
      syncAccountMappingData: syncAccountMappingData,
      enableSyncAccountData: enableSyncAccountData,
      showActionButtonsForSyncAccountDataSetting: false,
      syncAccountMappingDataError: {
        showErrorFieldApiName: false,
        showErrorValueApiName: false
      }
    })
  }

  createDefaultAccountMapping () {
    return {
      fieldApiName: '',
      valueApiName: ''
    }
  }

  render ({ isProduction }: Props, state: State) {
    const types = ['Sandbox', 'Production']
    const isVisibleFor = state.messageType === 'success' ? 5000 : false

    const getDeleteConfirmationButtons = () => {
      return [
        {
          text: 'Confirm',
          class: 'btn btn-danger qa-confirm',
          showLoading: true,
          callback: this.onDelete
        },
        {
          text: 'Cancel',
          class: 'btn btn-default',
          showLoading: false,
          callback: this.onHideConfirmationMessage
        }
      ]
    }

    return (
      <div class='integrations-salesforce'>
        <Header
          title='Salesforce'
          disableCancelButton
          disableSaveButton
          hasBreadcrumb
          breadcrumbText='Back to integrations overview'
          breadcrumbLink='/settings/integrations'
          statusLabelText={state.isInstalled ? 'Installed' : ''}
          statusLabelSuccess
        />
        <Panel title='Integration setup'>
          {
            state.messageType ? <AlertComponent messageType={state.messageType} message={state.message} sticky renderHTML isVisibleFor={isVisibleFor} /> : null
          }
          {!this.isSalesforceEnabled
            ? (<div className='qa-salesforce-integration-disabled'> The Salesforce integration is part of our
                Enterprise package. Please contact your CSM (or Support) to learn more. </div>)
            : (<div>
              <p className='integrations-salesforce__info'>Learn how to install the Salesforce integration step by
                  step. <button className='salesforce-configuration__note-text-btn' type='button' onClick={this.handleOpenEmbeddables}>Learn more</button></p>
              <a href={this.downloadLink} target='_blank'> <ButtonControl newStyle title='Download certificate' /> </a>
              <form className='integrations-salesforce__form' onSubmit={(e) => { e.preventDefault(); this.onSave() }}>
                <RadioInputGroup
                  title='Salesforce instance type *'
                  required
                  name='type'
                  disabled={state.isInstalled || state.isLoading}
                  options={types}
                  value={state.type}
                  onChange={this.onTypeChanged}
                />
                <InputGroup title='Salesforce username *'
                  type='text'
                  required
                  readOnly={state.isInstalled || state.isLoading ? 'readonly' : ''}
                  value={state.username}
                  onChange={this.onUsernameChanged}
                  qaClasses='qa-salesforce-username-input' />
                <div className='integrations-domain-wrapper'>
                  <div
                    className={`integrations-domain ${state.isInstalled || state.isLoading ? 'integrations-domain--disabled' : ''}`}>
                    .salesforce.com
                  </div>
                  <span className='integrations-domain-input'>
                    <InputGroup title='Instance URL *'
                      type='text'
                      required
                      readOnly={state.isInstalled || state.isLoading ? 'readonly' : ''}
                      qaClasses='qa-salesforce-instance-url-input'
                      value={state.instanceUrl}
                      onChange={this.onInstanceUrlChanged} />
                  </span>
                </div>
                <InputGroup title='Connected app consumer key *'
                  type='text'
                  required
                  readOnly={state.isInstalled || state.isLoading ? 'readonly' : ''}
                  value={state.appConsumerKey}
                  onChange={this.onAppConsumerKeyChanged}
                  qaClasses='qa-salesforce-app-consumer-key-input' />
                {state.isInstalled ? <ButtonControl newStyle variant='warning' class={'qa-delete'} title='Uninstall and delete credentials' onClick={this.onShowConfirmationMessage} />
                  : <ButtonControl newStyle variant='brand' type='submit' class={'qa-salesforce-submit'} title='Install' isLoading={state.isLoading} />}
              </form>
              <ConfirmationModal
                isLoading={state.isLoading}
                enableModal={state.showConfirmationMessage}
                title='Uninstall Salesforce integration?'
                message='Community data will no longer be pushed to your Salesforce instance.'
                buttons={getDeleteConfirmationButtons()}
                onClose={this.onHideConfirmationMessage}
              />
            </div>)
          }
        </Panel>
        {
          state.isInstalled ? (
            <div>
              { common.isModuleActive('salesforceCrm') && <Panel title='CRM' customClass='integrations-panel-with-margin' collapsible>
                <Switcher
                  on={state.enableCRM}
                  label='Enable CRM'
                  onChange={this.onCRMToggle} />
              </Panel> }
              { common.isModuleActive('salesforceCaseEscalation') && <Panel title='Case escalation' customClass='integrations-panel-with-margin' collapsible>
                <Switcher
                  on={state.enableEscalation}
                  label='Enable case escalation'
                  onChange={() => this.onCaseEscalationToggle()} />
              </Panel> }
              { common.isModuleActive('salesforceFederatedSearch') && <SalesforceFederatedSearch
                enableFederatedSearch={this.state.enableFederatedSearch}
                federatedSearchLanguage={this.state.federatedSearchLanguage}
                successCallback={(message) => { this.setState({ messageType: null }, () => this.setState({ message: message, messageType: 'success' })) }}
                errorCallback={(message) => { this.setState({ messageType: null }, () => this.setState({ message: message, messageType: 'error' })) }}
              /> }
              { common.isModuleActive('salesforceSyncAccountData') && <Panel title='Sync Salesforce account data to inSided' customClass='integrations-panel-with-margin' collapsible>
                { isProduction && state.type === 'Sandbox' ? <InformationCallout text={`It's not possible to sync Salesforce account data from a sandbox instance, please use a production instance instead to enable this use case. To test this integration with a sandbox instance, please log in to your inSided staging environment.`} variant='warning' />
                  : <div>
                    <InputGroup title='Account field API name'
                      type='text'
                      required
                      placeholder={'e.g. \'Type\', or \'Stage__c\''}
                      readOnly={state.isLoading ? 'readonly' : ''}
                      meta={'Provide a field and value which identifies whether a Salesforce Account is a Customer. The Account field you provide must be of type \'Picklist\'.'}
                      value={state.syncAccountMappingData.fieldApiName}
                      error={state.syncAccountMappingDataError.showErrorFieldApiName ? 'Please fill in the required field' : undefined}
                      onChange={this.onAccountMappingFieldApiNameChanged}
                      onClick={this.onSyncAccountMappingInputClicked}
                      qaClasses='qa-salesforce-account-field-api-name' />
                    <InputGroup title='Account picklist value API name'
                      type='text'
                      required
                      placeholder={'e.g. \'Customer\''}
                      readOnly={state.isLoading ? 'readonly' : ''}
                      meta={'Provide the API name of the value which identifies whether a Salesforce Account is a customer. This value must belong to the field you have provided above.'}
                      value={state.syncAccountMappingData.valueApiName}
                      error={state.syncAccountMappingDataError.showErrorValueApiName ? 'Please fill in the required field' : undefined}
                      onChange={this.onAccountMappingPicklistValueChanged}
                      onClick={this.onSyncAccountMappingInputClicked}
                      qaClasses='qa-salesforce-account-field-api-name' />
                    <Switcher
                      on={state.enableSyncAccountData}
                      customClass='salesforce-sync-account-data-toggle'
                      label='Enable Salesforce Account data sync to inSided'
                      onChange={() => this.onSyncAccountDataToggle()} />
                  </div> }
                {
                  state.showActionButtonsForSyncAccountDataSetting && <div>
                    <ButtonControl newStyle size='small' variant='save' title='Save changes' isLoading={state.isLoading} onClick={() => this.saveAccountSyncDataSettings()} />
                    <ButtonControl newStyle size='small' title='Cancel' onClick={() => this.resetAccountSyncDataSettings()} />
                  </div>
                }
              </Panel>
              }

            </div>
          ) : null
        }
      </div>
    )
  }
}

export default SalesforceIntegration
